import { type ReactNode } from 'react';

import { m } from 'motion/react';

import { Logo } from '../../primitives/Logo';

export interface AnimatedLogoProps {
  /** Custom logo element to use instead of the default */
  logo?: ReactNode;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

const SIZE_MAP = {
  sm: { container: 80, logo: 40, innerBorder: 60, outerBorder: 80 },
  md: { container: 120, logo: 64, innerBorder: 100, outerBorder: 120 },
  lg: { container: 160, logo: 80, innerBorder: 140, outerBorder: 160 },
};

/**
 * AnimatedLogo - Animated logo with pulsing and rotating border effects
 * Uses Motion for smooth animations matching the starter-vite-js design
 */
export function AnimatedLogo({ logo, size = 'md', className = '' }: AnimatedLogoProps) {
  const sizes = SIZE_MAP[size];

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: sizes.container, height: sizes.container }}
    >
      {/* Pulsing logo */}
      <m.div
        animate={{
          scale: [1, 0.9, 0.9, 1, 1],
          opacity: [1, 0.48, 0.48, 1, 1],
        }}
        transition={{
          duration: 2,
          repeatDelay: 1,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="inline-flex"
      >
        {logo ?? <Logo disableLink isSingle width={sizes.logo} height={sizes.logo} />}
      </m.div>

      {/* Inner rotating border */}
      <m.div
        animate={{
          scale: [1.6, 1, 1, 1.6, 1.6],
          rotate: [270, 0, 0, 270, 270],
          opacity: [0.25, 1, 1, 1, 0.25],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{
          ease: 'linear',
          duration: 3.2,
          repeat: Infinity,
        }}
        className="absolute border-[3px] border-[rgba(41,214,178,0.24)]"
        style={{
          width: sizes.innerBorder,
          height: sizes.innerBorder,
        }}
      />

      {/* Outer rotating border */}
      <m.div
        animate={{
          scale: [1, 1.2, 1.2, 1, 1],
          rotate: [0, 270, 270, 0, 0],
          opacity: [1, 0.25, 0.25, 0.25, 1],
          borderRadius: ['25%', '25%', '50%', '50%', '25%'],
        }}
        transition={{
          ease: 'linear',
          duration: 3.2,
          repeat: Infinity,
        }}
        className="absolute border-[8px] border-[rgba(41,214,178,0.24)]"
        style={{
          width: sizes.outerBorder,
          height: sizes.outerBorder,
        }}
      />
    </div>
  );
}
